import { useEffect } from "react";
import useThrottledXarrow from "./useThrottledXarrow";

/**
 * Observe <img> and <video> in `container` and update Xarrow when they load/resize,
 * also react to DOM mutations and element resizes.
 */
const useXarrowMediaObservers = (container: HTMLElement | null, anchorIds: string[] = []) => {
    const schedule = useThrottledXarrow();

    useEffect(() => {
        if (!container) return;

        // Observe size changes of container & anchors
        const ro = new ResizeObserver(() => schedule());
        ro.observe(container);
        anchorIds.forEach((id) => {
            const el = document.getElementById(id);
            if (el) ro.observe(el);
        });

        // Track added media so we can attach one-off listeners
        const unbind: Array<() => void> = [];

        const bindImg = (img: HTMLImageElement) => {
            if (img.dataset.xArrowBound) return;
            img.dataset.xArrowBound = "true";

            const done = () => schedule();
            if ("decode" in img && !img.complete) {
                img.decode().then(done).catch(done);
            }
            if (!img.complete) {
                img.addEventListener("load", done, { once: true });
                img.addEventListener("error", done, { once: true });
                unbind.push(() => {
                    img.removeEventListener("load", done);
                    img.removeEventListener("error", done);
                });
            } else {
                schedule();
            }
            ro.observe(img);
        };

        const bindVideo = (video: HTMLVideoElement) => {
            if (video.dataset.xArrowBound) return;
            video.dataset.xArrowBound = "true";

            const meta = () => schedule();
            // metadata gives us intrinsic video dimensions (affects layout)
            if (video.readyState >= 1 /* HAVE_METADATA */) {
                schedule();
            } else {
                video.addEventListener("loadedmetadata", meta, { once: true });
                unbind.push(() => video.removeEventListener("loadedmetadata", meta));
            }
            // Some UAs fire resize when rendered size changes
            const onResize = () => schedule();
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/no-explicit-any
            (video as any).onresize && video.addEventListener("resize", onResize);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            unbind.push(() => video.removeEventListener("resize", onResize as any));

            ro.observe(video);
        };

        // Initial bind for existing media
        container.querySelectorAll("img").forEach((n) => bindImg(n as HTMLImageElement));
        container.querySelectorAll("video").forEach((n) => bindVideo(n as HTMLVideoElement));

        // Watch for future media / structure changes
        const mo = new MutationObserver((muts) => {
            let changed = false;
            for (const m of muts) {
                m.addedNodes.forEach((node) => {
                    if (!(node instanceof HTMLElement)) return;
                    if (node.tagName === "IMG") bindImg(node as HTMLImageElement);
                    if (node.tagName === "VIDEO") bindVideo(node as HTMLVideoElement);
                    node.querySelectorAll?.("img").forEach((n) => bindImg(n as HTMLImageElement));
                    node.querySelectorAll?.("video").forEach((n) => bindVideo(n as HTMLVideoElement));
                    changed = true;
                });
            }
            if (changed) schedule();
        });
        mo.observe(container, { childList: true, subtree: true });

        // Fonts swapping can move text around headers/anchors
        document.fonts?.ready?.then?.(() => schedule());

        const onWin = () => schedule();
        window.addEventListener("resize", onWin);
        window.addEventListener("orientationchange", onWin);

        return () => {
            mo.disconnect();
            ro.disconnect();
            window.removeEventListener("resize", onWin);
            window.removeEventListener("orientationchange", onWin);
            unbind.forEach((fn) => fn());
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [container, schedule, JSON.stringify(anchorIds)]);
};

export default useXarrowMediaObservers;
